import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { FormProvider } from 'react-hook-form';
import { MaskedInput, FormRow as Row, FormSection as Section, Select, Toggle } from '~/components';
import { PeerReference } from '~/types';
import { TextInput } from '../../../components';
import { useCustomForm } from '../../../hooks/useCustomForm';
import { PEER_RELATIONSHIP_OPTIONS } from '../../vault/peer-reference-request/constants';
import { peerReferencePersonalSchema, PeerRequestPersonalFormValues, WizardProps } from '../types';
import { WizardFooter } from './WizardFooter';

const TAGS = CT.peerRef.personalForm;

export const PersonalForm = (props: WizardProps) => {
  const { actor } = props;
  const { personalInfo } = useSelector(actor, (state) => state.context);
  const methods = useCustomForm<PeerRequestPersonalFormValues>({
    resolver: zodResolver(peerReferencePersonalSchema),
    defaultValues: personalInfo,
  });

  const { isDirty } = methods.formState;

  const onPageSubmit = (values: PeerRequestPersonalFormValues) => {
    const partialPeerRef: Partial<PeerReference> = {
      emailAddress: values.emailAddress,
      faxNum: values.faxNum,
      firstName: values.firstName,
      lastName: values.lastName,
      occupation: values.occupation,
      phoneNum: values.phoneNum,
      prefixName: values.prefixName,
      relationshipType: values.relationshipType.value as PeerReference['relationshipType'],
      shareFormFlag: values.shareFormFlag,
      suffixName: values.suffixName,
    };
    actor.send({ type: 'ON_WIZ_UPDATE', values: partialPeerRef });
  };

  const nextBtn = {
    text: 'Next',
    onClick: async () => {
      if (isDirty) {
        methods.handleSubmit(onPageSubmit)();
      } else {
        const isValid = await methods.trigger();
        if (isValid) {
          actor.send({ type: 'ON_WIZ_NEXT_PAGE' });
        }
      }
    },
  };

  const prevBtn = {
    shouldShow: true,
    onClick: () => {
      actor.send({
        type: 'ON_WIZ_PREV_PAGE',
        draft: { kind: 'personal', values: methods.getValues() },
      });
    },
  };

  return (
    <>
      <FormProvider {...methods}>
        <form data-cy={TAGS.form} id="peer-req-form" className="leading-relaxed">
          <div className="mx-auto flex w-full max-w-3xl flex-col gap-5 pb-8 text-left">
            <h2 className="text-lg font-extrabold text-content-primary">
              Section I: Reference Information
            </h2>
            <Section componentsPerLine={1}>
              <Row>
                <TextInput name={'firstName'} cyTag={TAGS.firstName} label={'First Name*'} />
              </Row>
              <Row>
                <TextInput name={'lastName'} cyTag={TAGS.lastName} label={'Last Name*'} />
              </Row>
              <Row>
                <TextInput
                  name={'emailAddress'}
                  cyTag={TAGS.emailAddress}
                  label={'Email Address*'}
                />
              </Row>
              <Row>
                <MaskedInput
                  name={'phoneNum'}
                  label={'Phone Number'}
                  cyTag={TAGS.phoneNumber}
                  mask={'###-###-####'}
                  replacement={{ '#': /\d/ }}
                />
              </Row>
              <Row>
                <Select
                  name={'relationshipType'}
                  label={'Relationship'}
                  cyTag={TAGS.relationshipType}
                  options={PEER_RELATIONSHIP_OPTIONS}
                />
              </Row>
              <Row>
                <MaskedInput
                  name={'faxNum'}
                  label={'Fax Number'}
                  cyTag={TAGS.faxNumber}
                  mask={'###-###-####'}
                  replacement={{ '#': /\d/ }}
                />
              </Row>
              <Row>
                <TextInput name={'occupation'} cyTag={TAGS.occupation} label={'Occupation'} />
              </Row>
              <Row>
                <Toggle
                  name={'shareFormFlag'}
                  cyTag={TAGS.shareFormFlag}
                  label={'Do you want to share this with the person who sent this request?'}
                />
              </Row>
            </Section>
          </div>
        </form>
      </FormProvider>
      <WizardFooter nextBtn={nextBtn} prevBtn={prevBtn} />
    </>
  );
};
